'use client';

import { useQuery } from '@tanstack/react-query';
import {
  LeadEventsApi,
  LeadEventRosterMode,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Separator,
  Textarea,
  cn,
} from '@up2stay/up2promo-ui';
import { CalendarClock, MapPin } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import {
  addDaysToLocalInputValue,
  eventRangeDayCount,
  formatEventDurationLabel,
  isUnusuallyLongEventRange,
  isValidEventDateRange,
  LEAD_EVENT_NAME_MAX,
  toLocalInputValue,
} from '@/core/components/leads/lead-event-form-utils';
import { LeadEventRosterFields } from '@/core/components/leads/lead-event-roster-fields';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { teamsQueryKeys } from '@/core/query-keys/workspace-members-query-keys';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';

type CreateLeadEventDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onCreated: (eventId: string) => void;
};

function createDefaultEventDraft() {
  const start = new Date();
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 2);
  end.setHours(18, 0, 0, 0);
  return {
    name: '',
    location: '',
    description: '',
    startsAt: toLocalInputValue(start),
    endsAt: toLocalInputValue(end),
    rosterMode: LeadEventRosterMode.EVERYONE,
    selectedUserIds: [] as string[],
    selectedTeamIds: [] as string[],
  };
}

function FormSection({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function CreateLeadEventDialog({
  open,
  onOpenChange,
  workspaceId,
  onCreated,
}: CreateLeadEventDialogProps) {
  const dict = useDictionary().leads.events;
  const formDict = dict.form;
  const locale = useLocale();

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [rosterMode, setRosterMode] = useState<LeadEventRosterMode>(
    LeadEventRosterMode.EVERYONE,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const draft = createDefaultEventDraft();
      setName(draft.name);
      setLocation(draft.location);
      setDescription(draft.description);
      setStartsAt(draft.startsAt);
      setEndsAt(draft.endsAt);
      setRosterMode(draft.rosterMode);
      setSelectedUserIds(draft.selectedUserIds);
      setSelectedTeamIds(draft.selectedTeamIds);
      setShowValidation(false);
    }
  }

  const { data: members = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'event-roster',
    { enabled: open, staleTime: 60_000 },
  );

  const { data: teams = [] } = useQuery({
    queryKey: teamsQueryKeys.roster(workspaceId, 'event-roster'),
    queryFn: async () => {
      const res = await TeamsApi.findAll({
        path: { workspaceId },
        query: { page: 1, limit: 100 },
      });
      return (res.data?.data ?? []) as TeamWithCountsDto[];
    },
    enabled: open && !!workspaceId,
    staleTime: 60_000,
  });

  const rosterLabels = useMemo(
    () => ({
      rosterMode: formDict.rosterMode,
      rosterEveryone: formDict.rosterEveryone,
      rosterEveryoneHint: formDict.rosterEveryoneHint,
      rosterSelected: formDict.rosterSelected,
      rosterSelectedHint: formDict.rosterSelectedHint,
      selectUsers: formDict.selectUsers,
      selectTeams: formDict.selectTeams,
      searchMembers: formDict.searchMembers,
      searchTeams: formDict.searchTeams,
      noMembersMatch: formDict.noMembersMatch,
      noTeamsMatch: formDict.noTeamsMatch,
      resolvedCount: formDict.resolvedCount,
      resolvedCountOne: formDict.resolvedCountOne,
      rosterEmptyHint: formDict.rosterEmptyHint,
    }),
    [formDict],
  );

  const dateRangeValid = isValidEventDateRange(startsAt, endsAt);
  const showDateRangeError = Boolean(startsAt && endsAt) && !dateRangeValid;
  const rangeDayCount = eventRangeDayCount(startsAt, endsAt);
  const showLongRangeWarning = isUnusuallyLongEventRange(startsAt, endsAt);
  const durationLabel = formatEventDurationLabel(startsAt, endsAt, locale, {
    durationSameDay: formDict.durationSameDay,
    durationDays: formDict.durationDays,
  });

  const rosterSelectedEmpty =
    rosterMode === LeadEventRosterMode.SELECTED &&
    selectedUserIds.length === 0 &&
    selectedTeamIds.length === 0;

  const onSubmit = async () => {
    setShowValidation(true);
    if (!name.trim()) {
      nameInputRef.current?.focus();
      return;
    }
    if (!dateRangeValid || !startsAt || !endsAt) {
      document.getElementById('event-end')?.focus();
      return;
    }
    if (rosterSelectedEmpty) return;
    setSubmitting(true);
    try {
      const res = await LeadEventsApi.create({
        path: { workspaceId },
        body: {
          name: name.trim(),
          location: location.trim() || null,
          description: description.trim() || null,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          rosterMode,
          rosterUserIds:
            rosterMode === LeadEventRosterMode.SELECTED ? selectedUserIds : [],
          rosterTeamIds:
            rosterMode === LeadEventRosterMode.SELECTED ? selectedTeamIds : [],
        },
      });
      onOpenChange(false);
      if (res.data?.id) onCreated(res.data.id);
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (
              err as {
                response?: { status?: number; data?: { error?: string } };
              }
            ).response
          : undefined;
      if (status?.status === 409 || status?.data?.error === 'eventNameExists') {
        toast.error(formDict.errorNameTaken);
      } else {
        toast.error(formDict.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const durationPresets = [
    { key: '1d', label: formDict.durationPreset1Day, days: 1 },
    { key: '3d', label: formDict.durationPreset3Days, days: 3 },
    { key: '1w', label: formDict.durationPreset1Week, days: 7 },
  ] as const;

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="flex max-h-[min(90vh,720px)] w-full min-w-0 flex-col overflow-hidden sm:max-w-xl">
        <ResponsiveDialogHeader className="shrink-0">
          <ResponsiveDialogTitle>{formDict.createTitle}</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-1 py-1">
            <FormSection title={formDict.sectionBasics}>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="event-name">
                  {formDict.name}
                  <span className="ml-1 text-destructive" aria-hidden>
                    *
                  </span>
                </Label>
                <Input
                  id="event-name"
                  ref={nameInputRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={formDict.namePlaceholder}
                  maxLength={LEAD_EVENT_NAME_MAX}
                  autoFocus
                  aria-required
                  aria-invalid={showValidation && !name.trim()}
                  aria-describedby={
                    showValidation && !name.trim()
                      ? 'event-name-error'
                      : undefined
                  }
                />
                {showValidation && !name.trim() ? (
                  <p
                    id="event-name-error"
                    className="text-xs text-destructive"
                    role="alert"
                  >
                    {formDict.nameRequired}
                  </p>
                ) : null}
                <p className="text-right text-[12px] text-muted-foreground">
                  {formDict.nameCount
                    .replace('{count}', String(name.length))
                    .replace('{max}', String(LEAD_EVENT_NAME_MAX))}
                </p>
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="event-location">{formDict.location}</Label>
                <div className="relative">
                  <MapPin
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    id="event-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={formDict.locationPlaceholder}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="event-description">
                  {formDict.description}
                </Label>
                <Textarea
                  id="event-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={formDict.descriptionPlaceholder}
                  rows={3}
                  maxLength={5000}
                  className="min-h-[4.5rem] resize-y"
                />
              </div>
            </FormSection>

            <Separator />

            <FormSection title={formDict.sectionSchedule}>
              <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="event-start">{formDict.startsAt}</Label>
                  <Input
                    id="event-start"
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    className="w-full min-w-0"
                    aria-invalid={showDateRangeError}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="event-end">{formDict.endsAt}</Label>
                  <Input
                    id="event-end"
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="w-full min-w-0"
                    aria-invalid={showDateRangeError}
                  />
                </div>
              </div>

              {showDateRangeError ? (
                <p className="text-xs text-destructive" role="alert">
                  {formDict.dateRangeError}
                </p>
              ) : durationLabel ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {durationLabel}
                </p>
              ) : null}
              {showLongRangeWarning && rangeDayCount != null ? (
                <p className="text-xs text-muted-foreground" role="status">
                  {formDict.durationLongWarning.replace(
                    '{count}',
                    String(rangeDayCount),
                  )}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {durationPresets.map((preset) => (
                  <Button
                    key={preset.key}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-full px-3 text-xs"
                    disabled={!startsAt}
                    onClick={() =>
                      setEndsAt(addDaysToLocalInputValue(startsAt, preset.days))
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </FormSection>

            <Separator />

            <FormSection title={formDict.sectionTeam}>
              <LeadEventRosterFields
                rosterMode={rosterMode}
                onRosterModeChange={setRosterMode}
                selectedUserIds={selectedUserIds}
                onSelectedUserIdsChange={setSelectedUserIds}
                selectedTeamIds={selectedTeamIds}
                onSelectedTeamIdsChange={setSelectedTeamIds}
                members={members}
                teams={teams}
                labels={rosterLabels}
                locale={locale}
              />
            </FormSection>
          </div>

          <ResponsiveDialogFooter className="shrink-0 border-t border-border/60 pt-4 sm:justify-between">
            <p className="hidden text-xs text-muted-foreground sm:block">
              {formDict.autoTagHint}
            </p>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {formDict.cancel}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? formDict.saving : formDict.submit}
              </Button>
            </div>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
